/**
 * Target links (plan 2.3, owner 2026-10-09/12): the addresses posts send people to — a site, a Telegram
 * channel, anything — kept exactly as they go into posts. No redirect of ours ("не можна зв'язувати всі
 * акки одним доменом"), so clicks are not seen here; what is seen is every post that carried a link: on
 * which phones, accounts and directions, and when.
 *
 * Which link a post carries is read from its words (on the video, in the caption). Addresses found in
 * posts that are not on the list are offered to an admin to add — nothing is added by itself.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { TargetLink, UnlistedLink } from '../../api/types'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { Card, Empty, Status } from '../../ui/components'
import { DirectionChip, useDirectionName, useDirections } from './directions'

function errorText(e: unknown): string {
  const d = (e as { detail?: { detail?: { hint?: string } } })?.detail?.detail
  return d?.hint ?? (e instanceof Error ? e.message : String(e))
}

function when(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—'
}

function href(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

/** `direction` given — the links of that direction and the ones any direction may carry. */
export function LinksPanel({ direction: fixed }: { direction?: string } = {}) {
  const t = useT()
  const l = t.links
  const { api, auth, authEpoch } = useApp()
  const list = usePoll(() => api.links(), 60000, [api, authEpoch])
  const [problem, setProblem] = useState<string | null>(null)
  const links = (list.data?.links ?? []).filter((x) => !fixed || !x.direction_id || x.direction_id === fixed
    || (x.directions ?? []).includes(fixed))
  const live = links.filter((x) => !x.retired)
  const retired = links.filter((x) => x.retired)

  return (
    <>
      <Card title={l.title} sub={l.sub}>
        <p className="tiny dim">{l.rules}</p>
        {auth.isAdmin && <AddLink onAdded={list.refresh} onError={setProblem} />}
        <Status text={problem ?? (list.error ? String(list.error) : null)} bad />
      </Card>

      <Card title={l.listTitle} sub={l.listSub(live.length)}>
        {list.data && live.length === 0 && <Empty>{l.empty}</Empty>}
        <div className="link-list">
          {live.map((x) => <LinkRow key={x.link_id} link={x} warnAt={list.data?.shared_warn_phones ?? 5}
                                    onChanged={list.refresh} />)}
        </div>
        {retired.length > 0 && (
          <details className="link-retired">
            <summary className="tiny dim">{l.retiredList(retired.length)}</summary>
            <div className="link-list">
              {retired.map((x) => <LinkRow key={x.link_id} link={x} warnAt={list.data?.shared_warn_phones ?? 5}
                                           onChanged={list.refresh} />)}
            </div>
          </details>
        )}
      </Card>

      {(list.data?.unlisted.length ?? 0) > 0 && (
        <Card title={l.unlistedTitle} sub={l.unlistedSub}>
          <div className="link-list">
            {list.data!.unlisted.map((u) => (
              <UnlistedRow key={u.url} found={u} canAdd={auth.isAdmin} onAdded={list.refresh} />
            ))}
          </div>
        </Card>
      )}
    </>
  )
}

function DirectionPick({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT()
  const dirs = useDirections()
  const nameOf = useDirectionName()
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={t.links.direction}>
      <option value="">{t.links.anyDirection}</option>
      {dirs.list.map((d) => <option key={d.direction_id} value={d.direction_id}>{nameOf(d)}</option>)}
    </select>
  )
}

function AddLink({ onAdded, onError }: { onAdded: () => void; onError: (s: string | null) => void }) {
  const t = useT()
  const l = t.links
  const { api } = useApp()
  const [url, setUrl] = useState('')
  const [name, setName] = useState('')
  const [direction, setDirection] = useState('')
  const [busy, setBusy] = useState(false)
  const add = async () => {
    setBusy(true)
    onError(null)
    try {
      await api.addLink({ url: url.trim(), name: name.trim(), direction_id: direction || null })
      setUrl('')
      setName('')
      onAdded()
    } catch (e) {
      onError(errorText(e))
    } finally {
      setBusy(false)
    }
  }
  return (
    <form className="link-form" onSubmit={(e) => { e.preventDefault(); void add() }}>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder={l.urlPlaceholder} aria-label={l.urlPlaceholder} />
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={l.namePlaceholder} aria-label={l.namePlaceholder} />
      <DirectionPick value={direction} onChange={setDirection} />
      <button className="go" type="submit" disabled={busy || !url.trim()}>{l.add}</button>
    </form>
  )
}

function LinkRow({ link: x, warnAt, onChanged }: { link: TargetLink; warnAt: number; onChanged: () => void }) {
  const t = useT()
  const l = t.links
  const { api, auth } = useApp()
  const dirs = useDirections()
  const [problem, setProblem] = useState<string | null>(null)
  const direction = dirs.byId.get(x.direction_id ?? '')
  const edit = async (body: Parameters<typeof api.editLink>[1]) => {
    setProblem(null)
    try {
      await api.editLink(x.link_id, body)
      onChanged()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  const strangers = x.directions.filter((d) => x.direction_id && d !== x.direction_id)
  return (
    <div className={`link-row${x.retired ? ' retired' : ''}`}>
      <div className="link-main">
        <div className="link-head">
          {direction ? <DirectionChip direction={direction} /> : <span className="tiny dim">{l.anyDirection}</span>}
          {x.name && <b>{x.name}</b>}
          <a className="text-link mono link-url" href={href(x.url)} target="_blank" rel="noreferrer noopener">{x.url} ↗</a>
        </div>
        <div className="tiny">
          {x.publications > 0 ? (
            <Link className="text-link" to={`/publications?link=${encodeURIComponent(x.link_id)}`}>
              {l.carried(x.publications, x.phones, x.accounts)}
            </Link>
          ) : <span className="dim">{l.notPosted}</span>}
          {x.publications > 0 && <span className="dim"> · {l.span(when(x.first_posted), when(x.last_posted))}</span>}
        </div>
        {x.shared_widely && <div className="tiny warn-text">⚠ {l.sharedWidely(x.phones, warnAt)}</div>}
        {strangers.length > 0 && <div className="tiny warn-text">⚠ {l.otherDirection}</div>}
        {problem && <div className="tiny bad-text">{problem}</div>}
      </div>
      {auth.isAdmin && (
        <div className="link-tools">
          <DirectionPick value={x.direction_id ?? ''} onChange={(v) => void edit({ direction_id: v || null })} />
          <button className="quiet tiny" onClick={() => void edit({ retired: !x.retired })}>
            {x.retired ? l.unretire : l.retire}
          </button>
        </div>
      )}
    </div>
  )
}

function UnlistedRow({ found, canAdd, onAdded }: { found: UnlistedLink; canAdd: boolean; onAdded: () => void }) {
  const t = useT()
  const l = t.links
  const { api } = useApp()
  const [direction, setDirection] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const add = async () => {
    setProblem(null)
    try {
      await api.addLink({ url: found.url, direction_id: direction || null })
      onAdded()
    } catch (e) {
      setProblem(errorText(e))
    }
  }
  return (
    <div className="link-row">
      <div className="link-main">
        <span className="mono link-url">{found.url}</span>
        <div className="tiny dim">{l.foundIn(found.publications, when(found.last_posted))}</div>
        {problem && <div className="tiny bad-text">{problem}</div>}
      </div>
      {canAdd && (
        <div className="link-tools">
          <DirectionPick value={direction} onChange={setDirection} />
          <button className="tiny" onClick={() => void add()}>{l.addFound}</button>
        </div>
      )}
    </div>
  )
}
